import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './components/ui/card';
import { Button } from './components/ui/button';
import { Label } from './components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel, SelectSeparator } from './components/ui/select';
import { Users, CreditCard, TrendingUp, Plus, Download, Home, FileText, DollarSign, BarChart3, Map, UserCheck, FileCheck, Calendar as CalendarIcon, Settings, User, Calculator, CheckCircle, Bot, Star } from 'lucide-react';
import { LotForm } from './components/LotForm';
import { LotTable } from './components/LotTable';
import ConfiguracionProyecto from './components/ConfiguracionProyecto';
import { useAppData } from './hooks/useAppData';
import { VendedorModal, Vendedor } from './components/VendedorModal';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './components/ui/dialog';
import dayjs from 'dayjs';
import { LotMap } from './components/LotMap';
import { MovimientosTable } from './components/MovimientosTable';
import { Combobox } from './components/ui/combobox';
import { ComisionesVendedoresTable } from './components/ComisionesVendedoresTable';
import { ChartContainer } from './components/ui/chart';
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import { syncCrearAbono, syncEliminarAbono, syncAbonosGlobal } from './hooks/useSyncAbonos';
import { guardarLotePrueba, leerLotesPrueba } from "./firebaseTest";
import { firebaseConfig } from './firebaseConfig';
import { EstadoDeCuenta } from './components/EstadoDeCuenta';
import { EstadoCuentaBancario } from './components/EstadoCuentaBancario';
import { PaymentSystem } from './components/PaymentSystem';
import Devoluciones from './components/Devoluciones';
import LoginScreen from './components/LoginScreen';
import CompradorDashboard from './components/CompradorDashboard';
import VendedorDashboard from './components/VendedorDashboard';
import SocioDashboard from './components/SocioDashboard';
import UsuariosTable from './components/UsuariosTable';
import { ChatIA } from './components/ChatIA';
import { PublicChatIA } from './components/PublicChatIA';
import { ThemeProvider } from './components/ThemeProvider';
import { ThemeToggle } from './components/ThemeToggle';
import { LandingPage } from './components/LandingPage';
import PublicChatLogin from './components/PublicChatLogin';
import { Toaster } from 'sonner';
import { NotificationProvider } from './context/NotificationContext';
import { NotificationBanner } from './components/NotificationBanner';


// Utilidad: obtener abonos sincronizados entre payments del lote y expenses
function getAbonosSincronizados(lote: any, expenses: any[]): any[] {
  if (!lote.payments) return [];
  return lote.payments.filter((p: any) => p.type === 'abono' && expenses.some((e: any) => {
    const montoIgual = parseFloat(e.monto) === parseFloat(p.amount);
    const fechaIgual = (e.fecha && p.date) ? (new Date(e.fecha).toISOString().split('T')[0] === new Date(p.date).toISOString().split('T')[0]) : false;
    const tipoIgual = e.tipo === 'abono';
    const cuotaIgual = e.cuotaNumero ? e.cuotaNumero === p.cuotaNumero : true;
    const reciboIgual = (e.referencia && p.receipt) ? e.referencia.includes(p.receipt) : true;
    return montoIgual && fechaIgual && tipoIgual && cuotaIgual && reciboIgual;
  }));
}

// Normalizador de fecha a YYYY-MM-DD
function normalizeDate(dateStr: string) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toISOString().split('T')[0];
}

// Copiar la función robusta de abonos sincronizados aquí (idéntica a PaymentSystem)
function getAbonosSincronizadosRobusto(lot: any, expenses: any[]): any[] {
  if (!lot.payments) return [];
  return lot.payments.filter((p: any) => {
    if (p.type !== 'abono') return false;
    return expenses.some((e: any) => {
      let lotesExpense = e.lotes;
      if (typeof lotesExpense === 'string') lotesExpense = [lotesExpense];
      if (!Array.isArray(lotesExpense)) lotesExpense = [];
      const montoIgual = Math.abs(Number(e.monto) - Number(p.amount)) < 1;
      const tipoIngreso = e.tipo === 'ingreso';
      const fechaPago = new Date(p.date);
      const fechaExpense = new Date(e.fecha);
      const diffDias = Math.abs((fechaExpense.getTime() - fechaPago.getTime()) / (1000 * 60 * 60 * 24));
      const fechaIgual = diffDias <= 1;
      const referenciaIncluye = (e.referencia && (p.cuotaNumero ? e.referencia.includes(p.cuotaNumero) : false)) || (e.referencia && p.receipt && e.referencia.includes(p.receipt));
      const loteCoincide = lotesExpense.includes(lot.id?.toString());
      return montoIgual && tipoIngreso && fechaIgual && referenciaIncluye && loteCoincide;
    });
  });
}

// Definición de módulos para navegación y favoritos
const modules = [
  { id: 'dashboard', label: 'Dashboard', icon: Home, group: 'General', color: 'text-blue-500' },
  { id: 'lotes-registrados', label: 'Lotes Registrados', icon: FileText, group: 'Gestión', color: 'text-indigo-500' },
  { id: 'croquis-lotes', label: 'Croquis de Lotes', icon: Map, group: 'Gestión', color: 'text-cyan-500' },
  { id: 'vendedores', label: 'Vendedores', icon: UserCheck, group: 'Gestión', color: 'text-amber-500' },
  { id: 'usuarios', label: 'Usuarios', icon: User, group: 'Gestión', color: 'text-rose-500' },
  { id: 'configuraciones', label: 'Configuraciones', icon: Settings, group: 'Gestión', color: 'text-slate-500' },
  { id: 'info-financiera', label: 'Información Financiera', icon: BarChart3, group: 'Finanzas', color: 'text-orange-500' },
  { id: 'movimientos', label: 'Movimientos', icon: TrendingUp, group: 'Finanzas', color: 'text-emerald-500' },
  { id: 'cobros', label: 'Cobros', icon: DollarSign, group: 'Finanzas', color: 'text-green-500' },
  { id: 'estado-cuenta', label: 'Estado de Cuenta', icon: FileCheck, group: 'Finanzas', color: 'text-blue-400' },
  { id: 'estado-cuenta-bancario', label: 'Estado Cuenta Bancario', icon: CalendarIcon, group: 'Finanzas', color: 'text-purple-400' },
  { id: 'devoluciones', label: 'Devoluciones', icon: FileCheck, group: 'Finanzas', color: 'text-red-400' },
  { id: 'reporte-cobros', label: 'Reportes', icon: FileText, group: 'Reportes & Otros', color: 'text-muted-foreground' },
  { id: 'chat-ia', label: 'Asistente IA', icon: Bot, group: 'Reportes & Otros', color: 'text-purple-500' },
];

// Componente auxiliar para item de favoritos con lógica de drag & drop
const FavoriteItem = ({
  module,
  index,
  favorites,
  moveFavorite,
  activeTab,
  setActiveTab,
  draggedIndex,
  setDraggedIndex
}: {
  module: any,
  index: number,
  favorites: string[],
  moveFavorite: (from: number, to: number) => void,
  activeTab: string,
  setActiveTab: (tab: string) => void,
  draggedIndex: number | null,
  setDraggedIndex: (index: number | null) => void
}) => {
  const Icon = module.icon;
  const [isDraggable, setIsDraggable] = React.useState(false);
  const timerRef = React.useRef<NodeJS.Timeout | null>(null);

  const handleMouseDown = () => {
    timerRef.current = setTimeout(() => {
      setIsDraggable(true);
      if (navigator.vibrate) navigator.vibrate(50);
    }, 600);
  };

  const handleMouseUp = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (!isDraggable) {
      setIsDraggable(false);
    }
  };

  const handleMouseLeave = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
  };

  return (
    <div
      draggable={isDraggable}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseLeave}
      onDragStart={(e) => {
        if (!isDraggable) {
          e.preventDefault();
          return;
        }
        setDraggedIndex(index);
        e.dataTransfer.effectAllowed = 'move';
      }}
      onDragOver={(e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
      }}
      onDragEnter={(e) => {
        if (draggedIndex !== null && draggedIndex !== index) {
          moveFavorite(draggedIndex, index);
          setDraggedIndex(index);
        }
      }}
      onDragEnd={() => {
        setIsDraggable(false);
        setDraggedIndex(null);
      }}
      role="button"
      onClick={() => {
        if (!isDraggable) setActiveTab(module.id);
      }}
      className={`flex items-center gap-2 text-sm font-medium transition-colors select-none ${activeTab === module.id
        ? 'text-foreground'
        : 'text-muted-foreground hover:text-foreground'
        } ${isDraggable ? 'cursor-move opacity-50 scale-105' : 'cursor-pointer'}`}
    >
      <Icon className={`h-4 w-4 ${module.color}`} />
      <span className="hidden xl:inline">{module.label}</span>
    </div>
  );
};

// Componente para el dashboard principal (admin)
function MainDashboard() {
  // Estado de autenticación - leer de localStorage al iniciar
  const [user, setUser] = useState<null | { userType: string; userInput: string; pin?: string; password?: string }>(() => {
    const savedUser = localStorage.getItem('bimobiUser');
    return savedUser ? JSON.parse(savedUser) : null;
  });
  // Leer la pestaña activa de localStorage al iniciar
  const [activeTab, setActiveTabState] = useState<string>(() => localStorage.getItem('bimobiActiveTab') || 'dashboard');
  // Al cambiar de pestaña, guardar en localStorage
  const setActiveTab = (tab: string) => {
    setActiveTabState(tab);
    localStorage.setItem('bimobiActiveTab', tab);
  };

  // Estado para favoritos
  const [favorites, setFavorites] = useState<string[]>(() => {
    const saved = localStorage.getItem('bimobiFavorites');
    return saved ? JSON.parse(saved) : [];
  });

  const toggleFavorite = (moduleId: string) => {
    setFavorites(prev => {
      const newFavorites = prev.includes(moduleId)
        ? prev.filter(id => id !== moduleId)
        : [...prev, moduleId];
      localStorage.setItem('bimobiFavorites', JSON.stringify(newFavorites));
      return newFavorites;
    });
  };

  // Estado para el índice arrastrado (para live reordering)
  const [draggedIndex, setDraggedIndex] = React.useState<number | null>(null);

  const moveFavorite = (fromIndex: number, toIndex: number) => {
    const newFavorites = [...favorites];
    const [movedItem] = newFavorites.splice(fromIndex, 1);
    newFavorites.splice(toIndex, 0, movedItem);
    setFavorites(newFavorites);
    localStorage.setItem('bimobiFavorites', JSON.stringify(newFavorites));
  };
  const { usuarios, lots, lotGroups, sellers, addSeller, updateSeller, deleteSeller, expenses, lotHistory, markedPayments, config, setLots, setExpenses, comisiones, addSellerWithUser, saveAll } = useAppData();
  const [vendedorModalOpen, setVendedorModalOpen] = useState(false);
  const [vendedorEditando, setVendedorEditando] = useState<Vendedor | null>(null);
  const [vendedorAEliminar, setVendedorAEliminar] = useState<any | null>(null);
  const [selectedEgresoMonth, setSelectedEgresoMonth] = useState<string>('todos');
  // Estado de cuenta: hooks globales para búsqueda y selección
  const [busqueda, setBusqueda] = React.useState('');
  const [clienteSeleccionado, setClienteSeleccionado] = React.useState<string>("");
  const [mostrarEstado, setMostrarEstado] = React.useState(false);
  const [selectedCliente, setSelectedCliente] = React.useState<string>("");
  // 1. Agregar estado para controlar el modal de nuevo movimiento
  const [abrirNuevoMovimiento, setAbrirNuevoMovimiento] = useState(false);
  const [selectedMonthFinanciera, setSelectedMonthFinanciera] = React.useState('Todos');
  const [adminModalOpen, setAdminModalOpen] = useState(false);
  const [adminUser, setAdminUser] = useState('');
  const [adminPass, setAdminPass] = useState('');
  const [adminError, setAdminError] = useState('');

  // Validación robusta de usuario admin o contabilidad autenticado
  React.useEffect(() => {
    if (user && usuarios && usuarios.length > 0) {
      if (user.userType === 'admin') {
        const admin = usuarios.find((u: any) =>
          u.tipo === 'admin' &&
          u.nombre.toLowerCase().trim() === user.userInput.toLowerCase().trim() &&
          (!user.pin || u.pin === user.pin) &&
          u.activo &&
          !u.bloqueado
        );
        if (!admin) {
          // localStorage.removeItem('bimobiUser');
          // setUser(null);
          console.warn('Admin user validation failed but session kept active for debugging', user);
        }
      } else if (user.userType === 'contabilidad') {
        const contabilidad = usuarios.find((u: any) =>
          u.tipo === 'contabilidad' &&
          u.nombre.toLowerCase().trim() === user.userInput.toLowerCase().trim() &&
          u.password === user.password &&
          u.activo &&
          !u.bloqueado
        );
        if (!contabilidad) {
          // localStorage.removeItem('bimobiUser');
          // setUser(null);
          console.warn('Contabilidad user validation failed but session kept active for debugging', user);
        }
      }
    }
  }, [user, usuarios]);

  // Función para calcular saldo pendiente de un lote
  const calcularSaldoPendiente = (lote: any): number => {
    const totalValue = lote.totalValue || parseFloat(lote.valorTotal) || 0;
    const payments = Array.isArray(lote.payments) ? lote.payments : [];
    const pagado = payments
      .filter((p: any) => p.type === 'abono' || p.type === 'enganche')
      .reduce((sum: number, p: any) => sum + (parseFloat(p.amount) || 0), 0);
    return totalValue - pagado;
  };

  // Calcular estadísticas en tiempo real
  // Nuevo conteo robusto considerando ventas grupales
  const vendidos = lots
    .filter((lot: any) => lot.status === 'vendido')
    .reduce((sum: number, lot: any) => {
      if (Array.isArray(lot.idLotes) && lot.idLotes.length > 1) {
        return sum + lot.idLotes.length;
      }
      return sum + 1;
    }, 0);

  // Separar lotes a crédito entre pendientes y pagados
  const lotesCredito = lots.filter((lot: any) => lot.status === 'crédito');
  const creditoPendiente = lotesCredito
    .filter((lot: any) => calcularSaldoPendiente(lot) > 0.01) // Con saldo pendiente
    .reduce((sum: number, lot: any) => {
      if (Array.isArray(lot.idLotes) && lot.idLotes.length > 1) {
        return sum + lot.idLotes.length;
      }
      return sum + 1;
    }, 0);
  const creditoPagado = lotesCredito
    .filter((lot: any) => calcularSaldoPendiente(lot) <= 0.01) // Completamente pagados
    .reduce((sum: number, lot: any) => {
      if (Array.isArray(lot.idLotes) && lot.idLotes.length > 1) {
        return sum + lot.idLotes.length;
      }
      return sum + 1;
    }, 0);

  const totalLotes = config?.lotes || 0;
  const disponibles = totalLotes - vendidos - creditoPendiente - creditoPagado;
  const stats = {
    vendidos,
    disponibles: disponibles >= 0 ? disponibles : 0,
    credito: creditoPendiente, // Solo los que tienen saldo pendiente
    pagados: creditoPagado // Nueva categoría: completamente pagados
  };

  React.useEffect(() => {
    syncAbonosGlobal(lots, setLots, expenses, setExpenses);
    // eslint-disable-next-line
  }, []);

  // Función para manejar login exitoso
  const handleLoginSuccess = (userData: { userType: string; userInput: string; pin?: string }) => {
    console.log('MainDashboard handleLoginSuccess:', userData);
    setUser(userData);
    localStorage.setItem('bimobiUser', JSON.stringify(userData));
  };

  // Mostrar pantalla de login si no hay usuario autenticado
  if (!user) {
    return <LoginScreen userType="admin" onLoginSuccess={handleLoginSuccess} />;
  }

  // Si el usuario es contabilidad, solo mostrar la pestaña de movimientos
  if (user.userType === 'contabilidad') {
    return (
      <NotificationProvider>
        <div className="min-h-screen bg-background text-foreground">
          <header className="bg-card border-b border-border px-6 py-4">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
              <div>
                <h1 className="text-2xl font-semibold text-foreground tracking-tight">Bimobi</h1>
                <div className="text-sm text-muted-foreground">Movimientos</div>
              </div>
              <div className="flex items-center gap-2">
                <ThemeToggle />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setUser(null);
                    localStorage.removeItem('bimobiUser');
                    localStorage.removeItem('bimobiActiveTab');
                  }}
                  className="text-muted-foreground hover:text-foreground"
                >
                  Cerrar Sesión
                </Button>
              </div>
            </div>
          </header>
          <main className="p-6">
            <NotificationBanner />
            <MovimientosTable />
          </main>
        </div>
      </NotificationProvider>
    );
  }

  // Si el usuario no es admin, redirigir a su dashboard correspondiente
  if (user.userType !== 'admin') {
    const route = user.userType === 'comprador' ? 'compradores' : user.userType === 'vendedor' ? 'vendedores' : 'socios';
    return <Navigate to={`/${route}`} replace />;
  }

  // Si el usuario es admin, mostrar dashboard general con tabs y acceso total
  // (Eliminamos el return condicional exclusivo para admin)
  return (
    <NotificationProvider>
      <ThemeProvider defaultTheme="dark" storageKey="vite-ui-theme">
        <div className="min-h-screen bg-background text-foreground">
          {/* Header */}
          <header className="bg-card border-b border-border px-6 py-4">
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
              <div>
                <h1 className="text-xl font-semibold text-foreground tracking-tight">Bimobi</h1>
                <div className="text-[13px] text-muted-foreground">Sistema de Gestión Inmobiliaria – {config?.nombre} – {config?.ubicacion}</div>
                <div className="text-[11px] text-muted-foreground/60 mt-1">
                  {process.env.REACT_APP_ENV === 'production' ? 'Production' : 'Development'} · {firebaseConfig.projectId}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <ThemeToggle />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setUser(null);
                    localStorage.removeItem('bimobiUser');
                    localStorage.removeItem('bimobiActiveTab');
                  }}
                  className="bg-transparent border border-border hover:bg-accent text-foreground rounded-[6px] h-8 px-3 text-[13px] font-medium transition-colors"
                >
                  Cerrar Sesión
                </Button>
              </div>
            </div>
          </header>
          {/* Modal de login admin */}
          <Dialog open={adminModalOpen} onOpenChange={setAdminModalOpen}>
            <DialogContent className="max-w-xs">
              <DialogHeader>
                <DialogTitle>Acceso de Superusuario</DialogTitle>
              </DialogHeader>
              <form
                onSubmit={e => {
                  e.preventDefault();
                  // Buscar usuario admin en Firestore
                  const admin = usuarios.find((u: any) => u.tipo === 'admin' && u.nombre === adminUser && u.password === adminPass && u.activo && !u.bloqueado);
                  if (admin) {
                    const adminData = { userType: 'admin', userInput: admin.nombre, pin: '' };
                    setUser(adminData);
                    localStorage.setItem('bimobiUser', JSON.stringify(adminData));
                    setAdminModalOpen(false);
                    setAdminUser('');
                    setAdminPass('');
                    setAdminError('');
                  } else {
                    setAdminError('Usuario o contraseña incorrectos');
                  }
                }}
                className="space-y-4"
              >
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Usuario</label>
                  <input
                    type="text"
                    value={adminUser}
                    onChange={e => setAdminUser(e.target.value)}
                    className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-200"
                    autoFocus
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Contraseña</label>
                  <input
                    type="password"
                    value={adminPass}
                    onChange={e => setAdminPass(e.target.value)}
                    className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-200"
                  />
                </div>
                {adminError && <div className="text-red-600 text-sm">{adminError}</div>}
                <button
                  type="submit"
                  className="w-full py-2 rounded-lg font-semibold text-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors"
                >
                  Ingresar
                </button>
              </form>
            </DialogContent>
          </Dialog>

          {/* Navigation */}
          <nav className="bg-card border-b border-border px-6 py-3">
            <div className="flex items-center gap-4 overflow-x-auto no-scrollbar">
              <div className="flex items-center flex-shrink-0">
                <span className="text-sm text-muted-foreground mr-3">Ir a:</span>
                <Select value={activeTab} onValueChange={setActiveTab}>
                  <SelectTrigger className="w-[280px] bg-secondary border-border text-foreground focus:ring-ring h-10">
                    <SelectValue placeholder="Seleccionar vista" />
                  </SelectTrigger>
                  <SelectContent className="bg-popover border-border text-popover-foreground max-h-[400px]">
                    {['General', 'Gestión', 'Finanzas', 'Reportes & Otros'].map((group, index) => (
                      <React.Fragment key={group}>
                        {index > 0 && <SelectSeparator className="bg-border my-1" />}
                        <SelectGroup>
                          <SelectLabel className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-2 py-1.5">{group}</SelectLabel>
                          {modules.filter(m => m.group === group).map(module => (
                            <SelectItem key={module.id} value={module.id} className="focus:bg-accent focus:text-accent-foreground cursor-pointer pr-8 relative group">
                              <div className="flex items-center justify-between w-full">
                                <div className="flex items-center">
                                  <module.icon className={`h-4 w-4 mr-2 ${module.color}`} />
                                  <span>{module.label}</span>
                                </div>
                                <div
                                  role="button"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    toggleFavorite(module.id);
                                  }}
                                  className="dropdown-star absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-muted rounded-full transition-colors z-50"
                                >
                                  <Star
                                    className={`h-4 w-4 ${favorites.includes(module.id) ? 'fill-yellow-400 text-yellow-400' : 'text-muted-foreground/30 hover:text-yellow-400'}`}
                                  />
                                </div>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectGroup>
                      </React.Fragment>
                    ))}
                  </SelectContent>
                </Select>

                <div
                  role="button"
                  onClick={() => toggleFavorite(activeTab)}
                  className="ml-2 p-2 cursor-pointer text-muted-foreground hover:text-yellow-400 transition-colors"
                  title={favorites.includes(activeTab) ? "Quitar de favoritos" : "Agregar a favoritos"}
                >
                  <Star className={`h-5 w-5 ${favorites.includes(activeTab) ? 'fill-yellow-400 text-yellow-400' : ''}`} />
                </div>
              </div>

              {/* Favorites Bar */}
              {favorites.length > 0 && (
                <div className="flex items-center gap-4 pl-4 border-l border-border/50">
                  {favorites.map((favId, index) => {
                    const module = modules.find(m => m.id === favId);
                    if (!module) return null;
                    return (
                      <FavoriteItem
                        key={module.id}
                        module={module}
                        index={index}
                        favorites={favorites}
                        moveFavorite={moveFavorite}
                        activeTab={activeTab}
                        setActiveTab={setActiveTab}
                        draggedIndex={draggedIndex}
                        setDraggedIndex={setDraggedIndex}
                      />
                    );
                  })}
                </div>
              )}
            </div>
          </nav>

          {/* Main Content */}
          <main className="p-6">
            <NotificationBanner />
            {activeTab === 'dashboard' && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-6">
                  <Card className="bg-card border border-blue-500/20 shadow-none rounded-lg">
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-sm font-medium text-muted-foreground">Total de Lotes</CardTitle>
                      <TrendingUp className="h-4 w-4 text-blue-500" />
                    </CardHeader>
                    <CardContent>
                      <div className="text-3xl font-bold text-blue-500">{totalLotes}</div>
                      <p className="text-xs text-muted-foreground mt-1">Lotes configurados en el proyecto</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-card border border-green-500/20 shadow-none rounded-lg">
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-sm font-medium text-muted-foreground">Lotes Disponibles</CardTitle>
                      <Users className="h-4 w-4 text-green-500" />
                    </CardHeader>
                    <CardContent>
                      <div className="text-3xl font-bold text-green-500">{stats.disponibles}</div>
                      <p className="text-xs text-muted-foreground mt-1">Lotes listos para venta</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-card border border-orange-500/20 shadow-none rounded-lg">
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-sm font-medium text-muted-foreground">Lotes a Crédito</CardTitle>
                      <CreditCard className="h-4 w-4 text-orange-500" />
                    </CardHeader>
                    <CardContent>
                      <div className="text-3xl font-bold text-orange-500">{stats.credito}</div>
                      <p className="text-xs text-muted-foreground mt-1">En proceso de pago</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-card border border-emerald-500/20 shadow-none rounded-lg">
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-sm font-medium text-muted-foreground">Lotes Pagados</CardTitle>
                      <CheckCircle className="h-4 w-4 text-emerald-500" />
                    </CardHeader>
                    <CardContent>
                      <div className="text-3xl font-bold text-emerald-500">{stats.pagados}</div>
                      <p className="text-xs text-muted-foreground mt-1">Créditos completamente pagados</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-card border border-purple-500/20 shadow-none rounded-lg">
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-sm font-medium text-muted-foreground">Lotes de Contado</CardTitle>
                      <DollarSign className="h-4 w-4 text-purple-500" />
                    </CardHeader>
                    <CardContent>
                      <div className="text-3xl font-bold text-purple-500">{stats.vendidos}</div>
                      <p className="text-xs text-muted-foreground mt-1">Pagados al contado</p>
                    </CardContent>
                  </Card>
                </div>
                {/* Acciones Rápidas */}
                <Card className="bg-card border border-border shadow-none rounded-lg">
                  <CardHeader>
                    <CardTitle className="text-foreground">Acciones Rápidas</CardTitle>
                    <CardDescription className="text-muted-foreground">Accede rápidamente a las funciones más utilizadas</CardDescription>
                  </CardHeader>
                  <CardContent className="bg-card rounded-lg pt-0">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <Button
                        onClick={() => setActiveTab('registro')}
                        className="h-20 flex flex-col items-center justify-center space-y-2 border border-border bg-secondary text-muted-foreground rounded-lg shadow-none hover:bg-accent hover:text-accent-foreground transition-colors"
                        variant="ghost"
                      >
                        <Plus className="h-6 w-6" />
                        <span className="text-sm">Registrar Lote</span>
                      </Button>
                      <Button
                        onClick={() => {
                          setActiveTab('movimientos');
                          setAbrirNuevoMovimiento(true);
                        }}
                        className="h-20 flex flex-col items-center justify-center space-y-2 border border-border bg-secondary text-muted-foreground rounded-lg shadow-none hover:bg-accent hover:text-accent-foreground transition-colors"
                        variant="ghost"
                      >
                        <TrendingUp className="h-6 w-6" />
                        <span className="text-sm">Registrar Nuevo Movimiento</span>
                      </Button>
                      {/* Botón de Cobros eliminado por solicitud del usuario */}
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {activeTab === 'registro' && <LotForm setActiveTab={setActiveTab} />}
            {activeTab === 'lotes-registrados' && <LotTable setActiveTab={setActiveTab} />}
            {activeTab === 'configuraciones' && <ConfiguracionProyecto />}

            {activeTab === 'info-financiera' && (
              <Card className="bg-card border-border shadow-none rounded-lg overflow-hidden">
                <CardHeader className="pb-3 pt-4 px-4 border-b border-border/50">
                  <CardTitle className="text-xl font-semibold tracking-tight text-foreground flex items-center">
                    <BarChart3 className="h-5 w-5 mr-2" />
                    Información Financiera
                  </CardTitle>
                  <CardDescription className="text-[13px] text-muted-foreground mt-1">Análisis financiero completo del proyecto</CardDescription>
                </CardHeader>
                <CardContent className="bg-card rounded-xl pt-4">
                  {/* Selector de período de consulta */}
                  {(() => {
                    const months = ['Todos', 'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
                    return (
                      <div className="mb-6">
                        <div className="flex items-center gap-2 mb-2">
                          <CalendarIcon className="h-5 w-5 text-muted-foreground" />
                          <span className="font-semibold text-foreground">Período de Consulta</span>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {months.map((month, idx) => (
                            <button
                              key={month}
                              onClick={() => setSelectedMonthFinanciera(month)}
                              className={`px-4 py-2 text-sm font-medium border transition rounded-[6px] ${selectedMonthFinanciera === month ? 'bg-primary/10 text-primary border-primary/20' : 'bg-transparent text-foreground border-border hover:bg-accent'}`}
                            >
                              {month}
                            </button>
                          ))}
                        </div>
                      </div>
                    );
                  })()}
                  {/* Tarjeta de Flujo de Caja Proyectado (Próx. Mes) */}
                  {(() => {
                    // --- Lógica de cuotas del mes siguiente (idéntica a PaymentSystem) ---
                    const months = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
                    const now = new Date();
                    const mesActual = now.getMonth();
                    const anioActual = now.getFullYear();
                    const mesProximo = (mesActual + 1) % 12;
                    const anioProximo = mesActual === 11 ? anioActual + 1 : anioActual;
                    // Generar cuotas agrupadas por grupo de venta (idéntico a PaymentSystem)
                    const grupos: Record<string, any[]> = {};
                    lots.filter((lot: any) => lot.status === 'crédito' && lot.paymentType === 'pagos').forEach((lot: any) => {
                      const key = `${lot.seller}|${lot.buyerName}|${lot.buyerDPI}|${lot.saleDate}|${lot.receiptNumber}`;
                      if (!grupos[key]) grupos[key] = [];
                      grupos[key].push(lot);
                    });
                    let totalProyectado = 0;
                    Object.values(grupos).forEach((grupo) => {
                      if (grupo.length === 0) return;
                      const totalValue = grupo.reduce((sum, l) => sum + (l.totalValue || 0), 0);
                      const payments = grupo.flatMap(l => l.payments || []);
                      const numberOfPayments = grupo[0].numberOfPayments || grupo[0].numeroPagos || 1;
                      const pagoEnganche = payments.find((p: any) => p.type === 'enganche');
                      const enganche = pagoEnganche ? Number(pagoEnganche.amount) : 0;
                      const cuotaMensual = (totalValue - enganche) / numberOfPayments;
                      const fechaVentaDate = new Date(grupo[0].saleDate || new Date());
                      for (let i = 1; i <= numberOfPayments; i++) {
                        const fechaVencimiento = new Date(fechaVentaDate);
                        fechaVencimiento.setMonth(fechaVencimiento.getMonth() + i);
                        if (fechaVencimiento.getMonth() === mesProximo && fechaVencimiento.getFullYear() === anioProximo) {
                          totalProyectado += cuotaMensual;
                        }
                      }
                    });
                    // --- Fin lógica ---
                    return (
                      <>
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                          <Card className="md:col-span-2 bg-card border border-green-500/20 rounded-xl mb-8 flex flex-col justify-between">
                            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                              <CardTitle className="text-sm font-medium text-muted-foreground">Flujo de Caja Proyectado (Próx. Mes)</CardTitle>
                              <DollarSign className="h-5 w-5 text-green-500" />
                            </CardHeader>
                            <CardContent>
                              <div className="text-2xl font-bold text-foreground mb-1">Q. {totalProyectado.toLocaleString(undefined, { minimumFractionDigits: 3 })}</div>
                              <div className="text-xs text-muted-foreground">Ingresos estimados del próximo mes</div>
                            </CardContent>
                          </Card>
                          <Card className="md:col-span-2 bg-card border border-purple-500/20 rounded-xl mb-8 flex flex-col justify-between">
                            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                              <CardTitle className="text-sm font-medium text-muted-foreground">Avance General del Proyecto</CardTitle>
                              <TrendingUp className="h-5 w-5 text-purple-500" />
                            </CardHeader>
                            <CardContent>
                              {(() => {
                                const totalLotesProyecto = config?.lotes || lots.length;
                                // Usar la misma lógica robusta que se usa en otras partes del código
                                const vendidos = lots
                                  .filter((lot: any) => lot.status === 'vendido' || lot.status === 'crédito')
                                  .reduce((sum: number, lot: any) => {
                                    if (Array.isArray(lot.idLotes) && lot.idLotes.length > 1) {
                                      return sum + lot.idLotes.length;
                                    }
                                    return sum + 1;
                                  }, 0);
                                const porcentajeVendidos = totalLotesProyecto > 0 ? (vendidos / totalLotesProyecto) * 100 : 0;
                                return (
                                  <>
                                    <div className="text-2xl font-bold text-foreground mb-1">{porcentajeVendidos.toFixed(1)}%</div>
                                    <div className="text-xs text-muted-foreground">{vendidos} / {totalLotesProyecto} lotes vendidos</div>
                                  </>
                                );
                              })()}
                            </CardContent>
                          </Card>
                        </div>
                      </>
                    );
                  })()}
                  {/* Cálculos de métricas filtrados por mes */}
                  {(() => {
                    // --- NUEVO: Filtrar por mes seleccionado ---
                    const months = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
                    const monthIndex = months.indexOf(selectedMonthFinanciera);
                    // Filtrar movimientos por mes
                    const ingresos = expenses.filter((e: any) => ['ingreso', 'contado', 'enganche', 'abono'].includes((e.tipo || '').toLowerCase()) && (selectedMonthFinanciera === 'Todos' || (e.fecha && new Date(e.fecha).getMonth() === monthIndex)));
                    const egresos = expenses.filter((e: any) => ['egreso', 'comision'].includes((e.tipo || '').toLowerCase()) && (selectedMonthFinanciera === 'Todos' || (e.fecha && new Date(e.fecha).getMonth() === monthIndex)));
                    const totalIngresos = ingresos.reduce((sum: number, e: any) => sum + (parseFloat(e.monto) || 0), 0);
                    const totalEgresos = egresos.reduce((sum: number, e: any) => sum + (parseFloat(e.monto) || 0), 0);
                    const utilidadBruta = totalIngresos - totalEgresos;
                    // Proyección anual: suma de totalValue de lotes vendidos/crédito
                    const proyeccionAnual = lots.filter((l: any) => l.status === 'vendido' || l.status === 'crédito').reduce((sum: number, l: any) => sum + (l.totalValue || 0), 0);
                    // Agrupar ingresos por mes
                    const ingresosPorMes: { [k: string]: number } = {};
                    ingresos.forEach((e: any) => {
                      const mes = e.fecha ? months[new Date(e.fecha).getMonth()] : 'Sin fecha';
                      ingresosPorMes[mes] = (ingresosPorMes[mes] || 0) + (parseFloat(e.monto) || 0);
                    });
                    const egresosPorMes: { [k: string]: number } = {};
                    egresos.forEach((e: any) => {
                      const mes = e.fecha ? months[new Date(e.fecha).getMonth()] : 'Sin fecha';
                      egresosPorMes[mes] = (egresosPorMes[mes] || 0) + (parseFloat(e.monto) || 0);
                    });
                    // Promedio mensual
                    const mesesConIngresos = Object.keys(ingresosPorMes).length;
                    const promedioMensual = mesesConIngresos > 0 ? totalIngresos / mesesConIngresos : 0;
                    // Mejor mes
                    let mejorMes = '-';
                    let maxIngreso = 0;
                    Object.entries(ingresosPorMes).forEach(([mes, valor]) => {
                      if (valor > maxIngreso) { maxIngreso = valor; mejorMes = mes; }
                    });
                    // Eficiencia: % cobrado respecto a proyección
                    const eficiencia = proyeccionAnual > 0 ? (totalIngresos / proyeccionAnual) * 100 : 0;
                    // Estado de cobranza
                    const saldoPendiente = proyeccionAnual - totalIngresos;
                    // Datos para gráficas
                    const dataBar = months.map(mes => ({
                      mes,
                      Ingresos: ingresosPorMes[mes] || 0,
                      Egresos: egresosPorMes[mes] || 0
                    }));
                    const dataPie = [
                      { name: 'Cobrado', value: totalIngresos },
                      { name: 'Pendiente', value: saldoPendiente > 0 ? saldoPendiente : 0 }
                    ];
                    const pieColors = ['#10b981', '#f59e0b'];
                    return (
                      <>
                        {/* Cards */}
                        <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-4 gap-6 mb-8">
                          {/* Ingresos */}
                          <Card className="bg-card border border-green-500/20 rounded-xl flex flex-col justify-between">
                            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                              <CardTitle className="text-sm font-medium text-muted-foreground">Total Ingresos</CardTitle>
                              <DollarSign className="h-5 w-5 text-green-500" />
                            </CardHeader>
                            <CardContent>
                              <div className="text-2xl font-bold text-foreground">Q. {totalIngresos.toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
                              <p className="text-xs text-muted-foreground mt-1">Total de ingresos registrados</p>
                            </CardContent>
                          </Card>
                          {/* Egresos */}
                          <Card className="bg-card border border-red-500/20 rounded-xl flex flex-col justify-between">
                            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                              <CardTitle className="text-sm font-medium text-muted-foreground">Total Egresos</CardTitle>
                              <DollarSign className="h-5 w-5 text-red-500" />
                            </CardHeader>
                            <CardContent>
                              <div className="text-2xl font-bold text-foreground">Q. {totalEgresos.toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
                              <p className="text-xs text-muted-foreground mt-1">Total de egresos registrados</p>
                            </CardContent>
                          </Card>
                          {/* Utilidad Bruta */}
                          <Card className="bg-card border border-blue-500/20 rounded-xl flex flex-col justify-between">
                            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                              <CardTitle className="text-sm font-medium text-muted-foreground">Utilidad Bruta</CardTitle>
                              <TrendingUp className="h-5 w-5 text-blue-500" />
                            </CardHeader>
                            <CardContent>
                              <div className="text-2xl font-bold text-foreground">Q. {utilidadBruta.toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
                              <p className="text-xs text-muted-foreground mt-1">Ingresos menos egresos</p>
                            </CardContent>
                          </Card>
                          {/* Proyección Anual */}
                          <Card className="bg-card border border-orange-500/20 rounded-xl flex flex-col justify-between">
                            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                              <CardTitle className="text-sm font-medium text-muted-foreground">Proyección Anual</CardTitle>
                              <BarChart3 className="h-5 w-5 text-orange-500" />
                            </CardHeader>
                            <CardContent>
                              <div className="text-2xl font-bold text-foreground">Q. {proyeccionAnual.toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
                              <p className="text-xs text-muted-foreground mt-1">Suma de ventas a la fecha</p>
                            </CardContent>
                          </Card>
                          {/* Promedio Mensual */}
                          <Card className="bg-card border border-purple-500/20 rounded-xl flex flex-col justify-between">
                            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                              <CardTitle className="text-sm font-medium text-muted-foreground">Promedio Mensual</CardTitle>
                              <BarChart3 className="h-5 w-5 text-purple-500" />
                            </CardHeader>
                            <CardContent>
                              <div className="text-2xl font-bold text-foreground">Q. {promedioMensual.toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
                              <p className="text-xs text-muted-foreground mt-1">Promedio de ingresos por mes</p>
                            </CardContent>
                          </Card>
                          {/* Mejor Mes */}
                          <Card className="bg-card border border-green-500/20 rounded-xl flex flex-col justify-between">
                            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                              <CardTitle className="text-sm font-medium text-muted-foreground">Mejor Mes</CardTitle>
                              <CalendarIcon className="h-5 w-5 text-green-500" />
                            </CardHeader>
                            <CardContent>
                              <div className="text-2xl font-bold text-foreground">{mejorMes}</div>
                              <p className="text-xs text-muted-foreground mt-1">Mes con más ingresos</p>
                            </CardContent>
                          </Card>
                          {/* Eficiencia de Cobranza */}
                          <Card className="bg-card border border-blue-500/20 rounded-xl flex flex-col justify-between">
                            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                              <CardTitle className="text-sm font-medium text-muted-foreground">Eficiencia de Cobranza</CardTitle>
                              <TrendingUp className="h-5 w-5 text-blue-500" />
                            </CardHeader>
                            <CardContent>
                              <div className="text-2xl font-bold text-foreground">{eficiencia.toFixed(1)}%</div>
                              <p className="text-xs text-muted-foreground mt-1">% cobrado respecto a proyección</p>
                            </CardContent>
                          </Card>
                        </div>
                        {/* Gráficas */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                          <div className="bg-card border border-border rounded-xl p-4">
                            <h3 className="text-lg font-semibold mb-2 text-foreground">Ingresos vs Egresos Mensuales</h3>
                            <ResponsiveContainer width="100%" height={300}>
                              <BarChart data={dataBar} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                                <XAxis dataKey="mes" tick={{ fontSize: 12 }} />
                                <YAxis tick={{ fontSize: 12 }} />
                                <Tooltip formatter={(value: any) => `Q. ${parseFloat(value).toLocaleString(undefined, { minimumFractionDigits: 2 })}`} />
                                <Legend />
                                <Bar dataKey="Ingresos" fill="#10b981" radius={[4, 4, 0, 0]} />
                                <Bar dataKey="Egresos" fill="#f59e0b" radius={[4, 4, 0, 0]} />
                              </BarChart>
                            </ResponsiveContainer>
                          </div>
                          <div className="bg-card border border-border rounded-xl p-4">
                            <h3 className="text-lg font-semibold mb-2 text-foreground">Estado de Cobranza</h3>
                            <ResponsiveContainer width="100%" height={300}>
                              <PieChart>
                                <Pie data={dataPie} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label>
                                  {dataPie.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={pieColors[index % pieColors.length]} />
                                  ))}
                                </Pie>
                                <Tooltip formatter={(value: any) => `Q. ${parseFloat(value).toLocaleString(undefined, { minimumFractionDigits: 2 })}`} />
                                <Legend />
                              </PieChart>
                            </ResponsiveContainer>
                          </div>
                        </div>
                        {/* Indicadores adicionales */}
                        {(() => {
                          // 1. Cartera Vencida: suma de cuotas vencidas no pagadas
                          let carteraVencida = 0;
                          let totalCartera = 0;
                          let cuotasVencidas = 0;
                          let totalCuotas = 0;
                          const hoy = new Date();
                          lots.filter((l: any) => l.status === 'crédito' && l.payments && l.numberOfPayments).forEach((l: any) => {
                            const venta = l;
                            const enganche = (venta.payments || []).find((p: any) => p.type === 'enganche')?.amount || 0;
                            const saldo = (venta.totalValue || 0) - enganche;
                            const montoCuota = saldo / (venta.numberOfPayments || 1);
                            for (let i = 1; i <= (venta.numberOfPayments || 1); i++) {
                              const fechaVencimiento = new Date(venta.saleDate);
                              fechaVencimiento.setMonth(fechaVencimiento.getMonth() + i);
                              const fechaVencimientoYMD = fechaVencimiento.toISOString().split('T')[0];

                              // HOTFIX: Intentar lógica unificada con fallback a lógica anterior
                              let pagada = false;
                              try {
                                // Usar lógica unificada para verificar si la cuota está pagada
                                pagada = (venta.payments || []).some((p: any) => {
                                  if (p.type !== 'abono') return false;
                                  const sameCuota = String(p.cuotaNumero) === String(i);
                                  const pVenc = (p.cuotaFechaVencimiento || '').toString().slice(0, 10);
                                  const sameVenc = !p.cuotaFechaVencimiento || pVenc === fechaVencimientoYMD;
                                  return sameCuota && sameVenc;
                                });
                              } catch (error) {
                                // Fallback a lógica anterior si falla
                                console.warn('Hotfix fallback: usando lógica anterior para verificar cuota pagada', error);
                                pagada = (venta.payments || []).some((p: any) => p.type === 'abono' && p.cuotaNumero === i.toString());
                              }
                              if (!pagada) {
                                totalCartera += montoCuota;
                                totalCuotas++;
                                if (fechaVencimiento < hoy) {
                                  carteraVencida += montoCuota;
                                  cuotasVencidas++;
                                }
                              }
                            }
                          });
                          // 2. % Cartera Vencida
                          const porcentajeCarteraVencida = totalCartera > 0 ? (carteraVencida / totalCartera) * 100 : 0;
                          // 3. Ingresos Realizados vs Esperados: ya calculado arriba
                          // 4. Tasa de Recuperación: eficiencia (ya calculada)
                          // 5. Rentabilidad promedio por lote (si hay costo)
                          let rentabilidadPromedio = '-';
                          if (lots.some((l: any) => l.costo)) {
                            const lotesConCosto = lots.filter((l: any) => l.costo);
                            const totalIngreso = lotesConCosto.reduce((sum: number, l: any) => sum + (l.totalValue || 0), 0);
                            const totalCosto = lotesConCosto.reduce((sum: number, l: any) => sum + (l.costo || 0), 0);
                            rentabilidadPromedio = totalCosto > 0 ? (((totalIngreso - totalCosto) / totalCosto) * 100).toFixed(1) : '-';
                          }
                          // 6. Flujo de caja proyectado (próximo mes, solo cuotas NO pagadas)
                          let flujoCajaProximoMes = 0;
                          const proximoMes = new Date(hoy.getFullYear(), hoy.getMonth() + 1, 1);
                          lots.filter((l: any) => l.status === 'crédito' && l.numberOfPayments).forEach((l: any) => {
                            const venta = l;
                            const enganche = (venta.payments || []).find((p: any) => p.type === 'enganche')?.amount || 0;
                            const saldo = (venta.totalValue || 0) - enganche;
                            const montoCuota = saldo / (venta.numberOfPayments || 1);
                            for (let i = 1; i <= (venta.numberOfPayments || 1); i++) {
                              const fechaVencimiento = new Date(venta.saleDate);
                              fechaVencimiento.setMonth(fechaVencimiento.getMonth() + i);
                              if (
                                fechaVencimiento.getMonth() === proximoMes.getMonth() &&
                                fechaVencimiento.getFullYear() === proximoMes.getFullYear()
                              ) {
                                // Verificar si la cuota ya fue pagada
                                const fechaVencimientoYMD = fechaVencimiento.toISOString().split('T')[0];
                                const pagada = (venta.payments || []).some((p: any) => {
                                  if (p.type !== 'abono') return false;
                                  const sameCuota = String(p.cuotaNumero) === String(i);
                                  const pVenc = (p.cuotaFechaVencimiento || '').toString().slice(0, 10);
                                  const sameVenc = !p.cuotaFechaVencimiento || pVenc === fechaVencimientoYMD;
                                  return sameCuota && sameVenc;
                                });
                                // Solo sumar si NO está pagada
                                if (!pagada) {
                                  flujoCajaProximoMes += montoCuota;
                                }
                              }
                            }
                          });
                          // 7. Índice de rotación de lotes
                          const lotesVendidos = lots.filter((l: any) => l.status === 'vendido' || l.status === 'crédito').length;
                          const inventarioInicial = config?.lotes || 0;
                          const rotacionLotes = inventarioInicial > 0 ? (lotesVendidos / inventarioInicial) * 100 : 0;
                          // 8. Promedio de días de cobro
                          let sumaDiasCobro = 0;
                          let numCobros = 0;
                          lots.filter((l: any) => l.status === 'crédito' && l.payments && l.numberOfPayments).forEach((l: any) => {
                            (l.payments || []).filter((p: any) => p.type === 'abono' && p.cuotaNumero && p.cuotaFechaVencimiento).forEach((p: any) => {
                              const fechaVenc = p.cuotaFechaVencimiento ? new Date(p.cuotaFechaVencimiento.split('/').reverse().join('-')) : null;
                              const fechaPago = p.date ? new Date(p.date) : null;
                              if (fechaVenc && fechaPago) {
                                const diff = (fechaPago.getTime() - fechaVenc.getTime()) / (1000 * 60 * 60 * 24);
                                sumaDiasCobro += diff;
                                numCobros++;
                              }
                            });
                          });
                          const promedioDiasCobro = numCobros > 0 ? (sumaDiasCobro / numCobros).toFixed(1) : '-';
                          // 9. Utilidad neta (si hay gastos operativos)
                          let utilidadNeta = '-';
                          const gastosOperativos = expenses.filter((e: any) => e.tipo === 'gasto' || e.tipo === 'operativo').reduce((sum: number, e: any) => sum + (parseFloat(e.monto) || 0), 0);
                          if (gastosOperativos > 0) {
                            utilidadNeta = (utilidadBruta - gastosOperativos).toLocaleString(undefined, { minimumFractionDigits: 2 });
                          }
                          return (
                            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 mb-8">
                              {/* 1. Cartera Vencida */}
                              <div className="bg-card border border-border rounded-xl p-4 flex flex-col">
                                <span className="font-semibold text-foreground">Cartera Vencida</span>
                                <span className="text-lg font-bold text-foreground">Q. {carteraVencida.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                                <span className="text-xs text-muted-foreground mt-1">Monto total de pagos vencidos y no cobrados</span>
                              </div>
                              {/* 2. % Cartera Vencida */}
                              <div className="bg-card border border-border rounded-xl p-4 flex flex-col">
                                <span className="font-semibold text-foreground">% Cartera Vencida</span>
                                <span className="text-lg font-bold text-foreground">{porcentajeCarteraVencida.toFixed(1)}%</span>
                                <span className="text-xs text-muted-foreground mt-1">Porcentaje de morosidad respecto al total de cartera</span>
                              </div>
                              {/* 3. Ingresos Realizados vs Esperados */}
                              <div className="bg-card border border-border rounded-xl p-4 flex flex-col">
                                <span className="font-semibold text-foreground">Ingresos Realizados / Esperados</span>
                                <span className="text-lg font-bold text-foreground">Q. {totalIngresos.toLocaleString(undefined, { minimumFractionDigits: 2 })} / Q. {proyeccionAnual.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                                <span className="text-xs text-muted-foreground mt-1">Comparativo de lo cobrado vs lo esperado</span>
                              </div>
                              {/* 4. Tasa de Recuperación */}
                              <div className="bg-card border border-border rounded-xl p-4 flex flex-col">
                                <span className="font-semibold text-foreground">Tasa de Recuperación</span>
                                <span className="text-lg font-bold text-foreground">{eficiencia.toFixed(1)}%</span>
                                <span className="text-xs text-muted-foreground mt-1">Cobrado respecto a ventas a crédito</span>
                              </div>
                              {/* 5. Rentabilidad por Lote (promedio) */}
                              <div className="bg-card border border-border rounded-xl p-4 flex flex-col">
                                <span className="font-semibold text-foreground">Rentabilidad Promedio por Lote</span>
                                <span className="text-lg font-bold text-foreground">{rentabilidadPromedio}{typeof rentabilidadPromedio === 'string' && rentabilidadPromedio !== '-' ? '%' : ''}</span>
                                <span className="text-xs text-muted-foreground mt-1">(Ingreso - Costo) / Costo promedio</span>
                              </div>
                              {/* 6. Flujo de Caja Proyectado (próximo mes) */}
                              {/* Eliminado: ya se muestra en la sección principal de métricas */}
                              {/* 7. Índice de Rotación de Lotes */}
                              <div className="bg-card border border-border rounded-xl p-4 flex flex-col">
                                <span className="font-semibold text-foreground">Índice de Rotación de Lotes</span>
                                <span className="text-lg font-bold text-foreground">{rotacionLotes.toFixed(1)}%</span>
                                <span className="text-xs text-muted-foreground mt-1">Lotes vendidos respecto al inventario inicial</span>
                              </div>
                              {/* 8. Promedio de Días de Cobro */}
                              <div className="bg-card border border-border rounded-xl p-4 flex flex-col">
                                <span className="font-semibold text-foreground">Promedio de Días de Cobro</span>
                                <span className="text-lg font-bold text-foreground">{promedioDiasCobro}</span>
                                <span className="text-xs text-muted-foreground mt-1">Días promedio para cobrar una cuota</span>
                              </div>
                              {/* 9. Utilidad Neta */}
                              <div className="bg-card border border-border rounded-xl p-4 flex flex-col">
                                <span className="font-semibold text-foreground">Utilidad Neta</span>
                                <span className="text-lg font-bold text-foreground">{utilidadNeta !== '-' ? `Q. ${utilidadNeta}` : '-'}</span>
                                <span className="text-xs text-muted-foreground mt-1">Utilidad después de gastos operativos</span>
                              </div>
                              {/* 10. Velocidad de Ventas */}
                              {(() => {
                                const primerVenta = lots.filter((l: any) => l.status === 'vendido' || l.status === 'crédito')
                                  .map((l: any) => new Date(l.saleDate))
                                  .sort((a: Date, b: Date) => a.getTime() - b.getTime())[0];
                                const mesesTranscurridos = primerVenta ?
                                  ((hoy.getTime() - primerVenta.getTime()) / (1000 * 60 * 60 * 24 * 30)) : 1;
                                const lotesVendidosTotal = lots.filter((l: any) => l.status === 'vendido' || l.status === 'crédito')
                                  .reduce((sum: number, l: any) => {
                                    if (Array.isArray(l.idLotes) && l.idLotes.length > 1) return sum + l.idLotes.length;
                                    return sum + 1;
                                  }, 0);
                                const velocidadVentas = mesesTranscurridos > 0 ? lotesVendidosTotal / mesesTranscurridos : 0;
                                const disponiblesActual = (config?.lotes || 0) - lotesVendidosTotal;
                                const mesesParaAgotar = velocidadVentas > 0 ? disponiblesActual / velocidadVentas : 0;
                                return (
                                  <div className="bg-card border border-border rounded-xl p-4 flex flex-col">
                                    <span className="font-semibold text-foreground">Velocidad de Ventas</span>
                                    <span className="text-lg font-bold text-foreground">{velocidadVentas.toFixed(1)} lotes/mes</span>
                                    <span className="text-xs text-muted-foreground mt-1">
                                      {mesesParaAgotar > 0 ? `Inventario se agotará en ${mesesParaAgotar.toFixed(1)} meses` : 'Sin proyección'}
                                    </span>
                                  </div>
                                );
                              })()}
                              {/* 11. Ticket Promedio por Venta */}
                              {(() => {
                                const ventasRealizadas = lots.filter((l: any) => l.status === 'vendido' || l.status === 'crédito');
                                const totalVentas = ventasRealizadas.reduce((sum: number, l: any) => sum + (l.totalValue || 0), 0);
                                const ticketPromedio = ventasRealizadas.length > 0 ? totalVentas / ventasRealizadas.length : 0;
                                return (
                                  <div className="bg-card border border-border rounded-xl p-4 flex flex-col">
                                    <span className="font-semibold text-foreground">Ticket Promedio por Venta</span>
                                    <span className="text-lg font-bold text-foreground">Q. {ticketPromedio.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                                    <span className="text-xs text-muted-foreground mt-1">Valor promedio por transacción</span>
                                  </div>
                                );
                              })()}
                              {/* 12. Cuotas por Vencer (Próximos 30 días) */}
                              {(() => {
                                let cuotasPorVencer7 = 0, cuotasPorVencer15 = 0, cuotasPorVencer30 = 0;
                                let montoPorVencer7 = 0, montoPorVencer15 = 0, montoPorVencer30 = 0;
                                const hoyTime = hoy.getTime();
                                const dias7 = 7 * 24 * 60 * 60 * 1000;
                                const dias15 = 15 * 24 * 60 * 60 * 1000;
                                const dias30 = 30 * 24 * 60 * 60 * 1000;

                                lots.filter((l: any) => l.status === 'crédito' && l.numberOfPayments).forEach((l: any) => {
                                  const enganche = (l.payments || []).find((p: any) => p.type === 'enganche')?.amount || 0;
                                  const saldo = (l.totalValue || 0) - enganche;
                                  const montoCuota = saldo / (l.numberOfPayments || 1);

                                  for (let i = 1; i <= (l.numberOfPayments || 1); i++) {
                                    const fechaVencimiento = new Date(l.saleDate);
                                    fechaVencimiento.setMonth(fechaVencimiento.getMonth() + i);
                                    const fechaVencimientoYMD = fechaVencimiento.toISOString().split('T')[0];

                                    // Verificar si ya fue pagada
                                    const pagada = (l.payments || []).some((p: any) => {
                                      if (p.type !== 'abono') return false;
                                      const sameCuota = String(p.cuotaNumero) === String(i);
                                      const pVenc = (p.cuotaFechaVencimiento || '').toString().slice(0, 10);
                                      const sameVenc = !p.cuotaFechaVencimiento || pVenc === fechaVencimientoYMD;
                                      return sameCuota && sameVenc;
                                    });

                                    if (!pagada && fechaVencimiento > hoy) {
                                      const diffTime = fechaVencimiento.getTime() - hoyTime;
                                      if (diffTime <= dias7) {
                                        cuotasPorVencer7++;
                                        montoPorVencer7 += montoCuota;
                                      } else if (diffTime <= dias15) {
                                        cuotasPorVencer15++;
                                        montoPorVencer15 += montoCuota;
                                      } else if (diffTime <= dias30) {
                                        cuotasPorVencer30++;
                                        montoPorVencer30 += montoCuota;
                                      }
                                    }
                                  }
                                });

                                return (
                                  <div className="bg-card border border-border rounded-xl p-4 flex flex-col">
                                    <span className="font-semibold text-foreground">Cuotas por Vencer</span>
                                    <div className="mt-2 space-y-1">
                                      <div className="flex justify-between text-sm">
                                        <span className="text-muted-foreground">7 días:</span>
                                        <span className="font-bold text-red-600">{cuotasPorVencer7} (Q. {montoPorVencer7.toLocaleString(undefined, { minimumFractionDigits: 0 })})</span>
                                      </div>
                                      <div className="flex justify-between text-sm">
                                        <span className="text-muted-foreground">15 días:</span>
                                        <span className="font-bold text-orange-600">{cuotasPorVencer15} (Q. {montoPorVencer15.toLocaleString(undefined, { minimumFractionDigits: 0 })})</span>
                                      </div>
                                      <div className="flex justify-between text-sm">
                                        <span className="text-muted-foreground">30 días:</span>
                                        <span className="font-bold text-yellow-600">{cuotasPorVencer30} (Q. {montoPorVencer30.toLocaleString(undefined, { minimumFractionDigits: 0 })})</span>
                                      </div>
                                    </div>
                                    <span className="text-xs text-muted-foreground mt-2">Cuotas pendientes próximas a vencer</span>
                                  </div>
                                );
                              })()}
                              {/* 13. Clientes Morosos Activos */}
                              {(() => {
                                const clientesMorosos: any[] = [];
                                const clientesMap: { [key: string]: any } = {};

                                lots.filter((l: any) => l.status === 'crédito' && l.numberOfPayments).forEach((l: any) => {
                                  const enganche = (l.payments || []).find((p: any) => p.type === 'enganche')?.amount || 0;
                                  const saldo = (l.totalValue || 0) - enganche;
                                  const montoCuota = saldo / (l.numberOfPayments || 1);
                                  let cuotasVencidas = 0;
                                  let montoVencido = 0;

                                  for (let i = 1; i <= (l.numberOfPayments || 1); i++) {
                                    const fechaVencimiento = new Date(l.saleDate);
                                    fechaVencimiento.setMonth(fechaVencimiento.getMonth() + i);
                                    const fechaVencimientoYMD = fechaVencimiento.toISOString().split('T')[0];

                                    const pagada = (l.payments || []).some((p: any) => {
                                      if (p.type !== 'abono') return false;
                                      const sameCuota = String(p.cuotaNumero) === String(i);
                                      const pVenc = (p.cuotaFechaVencimiento || '').toString().slice(0, 10);
                                      const sameVenc = !p.cuotaFechaVencimiento || pVenc === fechaVencimientoYMD;
                                      return sameCuota && sameVenc;
                                    });

                                    if (!pagada && fechaVencimiento < hoy) {
                                      cuotasVencidas++;
                                      montoVencido += montoCuota;
                                    }
                                  }

                                  // Cliente moroso si tiene 2+ cuotas vencidas
                                  if (cuotasVencidas >= 2) {
                                    const key = l.buyerName || 'Sin nombre';
                                    if (clientesMap[key]) {
                                      clientesMap[key].cuotasVencidas += cuotasVencidas;
                                      clientesMap[key].montoVencido += montoVencido;
                                    } else {
                                      clientesMap[key] = {
                                        nombre: key,
                                        cuotasVencidas,
                                        montoVencido
                                      };
                                    }
                                  }
                                });

                                Object.values(clientesMap).forEach((value: any) => clientesMorosos.push(value));
                                clientesMorosos.sort((a: any, b: any) => b.montoVencido - a.montoVencido);

                                return (
                                  <div className="bg-card border border-border rounded-xl p-4 flex flex-col">
                                    <span className="font-semibold text-foreground">Clientes Morosos Activos</span>
                                    <span className="text-lg font-bold text-foreground">{clientesMorosos.length}</span>
                                    <span className="text-xs text-muted-foreground mt-1">
                                      Clientes con 2+ cuotas vencidas
                                      {clientesMorosos.length > 0 && (
                                        <span className="block mt-1 font-medium">
                                          Top: {clientesMorosos[0].nombre.substring(0, 20)} (Q. {clientesMorosos[0].montoVencido.toLocaleString(undefined, { minimumFractionDigits: 0 })})
                                        </span>
                                      )}
                                    </span>
                                  </div>
                                );
                              })()}
                            </div>
                          );
                        })()}
                      </>
                    );
                  })()}
                </CardContent>
              </Card>
            )}

            {activeTab === 'croquis-lotes' && (
              <Card className="bg-card border-border shadow-none rounded-lg overflow-hidden">
                <CardHeader className="pb-3 pt-4 px-4 border-b border-border/50">
                  <CardTitle className="text-xl font-semibold tracking-tight text-orange-400 flex items-center">
                    <Map className="h-5 w-5 mr-2" />
                    Croquis de Lotes
                  </CardTitle>
                  <CardDescription className="text-[13px] text-muted-foreground mt-1">Visualización gráfica de la distribución de lotes</CardDescription>
                </CardHeader>
                <CardContent className="pt-4">
                  <LotMap lots={lots} config={config} />
                </CardContent>
              </Card>
            )}

            {activeTab === 'vendedores' && (
              <>
                <div className="flex justify-between items-center mb-2">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">Gestión de Vendedores</h2>
                    <p className="text-gray-600 mt-1">Administra la información de los vendedores</p>
                  </div>
                  <Button
                    variant="default"
                    className="bg-black text-white hover:bg-gray-900 focus:bg-black rounded-xl px-6 py-2 flex items-center gap-2"
                    onClick={() => { setVendedorEditando(null); setVendedorModalOpen(true); }}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Agregar Vendedor
                  </Button>
                </div>
                <ComisionesVendedoresTable lots={lots} sellers={sellers} />
                <div className="space-y-4">
                  <div className="flex justify-between items-center mb-2">
                    <h3 className="text-lg font-semibold">Vendedores Activos ({sellers.length})</h3>
                    <Button
                      variant="default"
                      className="bg-black text-white hover:bg-gray-900 focus:bg-black rounded-xl px-6 py-2 flex items-center gap-2"
                      onClick={() => { setVendedorEditando(null); setVendedorModalOpen(true); }}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Agregar Vendedor
                    </Button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-2 gap-6">
                    {sellers.map((vendedor: any) => {
                      // Comisiones por este vendedor en la colección 'comisiones' (Firestore)
                      const comisionesVendedor = comisiones.filter((c: any) => c.sellerId === vendedor.account || c.sellerName === vendedor.name);
                      const comisionesPagadasArr = comisionesVendedor.filter((c: any) => c.paid);
                      const comisionesPendientesArr = comisionesVendedor.filter((c: any) => !c.paid);
                      const comisionesPagadas = comisionesPagadasArr.reduce((sum: number, c: any) => sum + (parseFloat(c.commissionAmount) || 0), 0);
                      const comisionesPendientes = comisionesPendientesArr.reduce((sum: number, c: any) => sum + (parseFloat(c.commissionAmount) || 0), 0);
                      const totalComisiones = comisionesPagadas + comisionesPendientes;
                      // Lotes vendidos por este vendedor (match por nombre o account)
                      const lotesVendidos = expenses.filter((e: any) => e.tipo === 'comision' && (e.vendedor === vendedor.name || e.vendedor === vendedor.account)).flatMap((e: any) => Array.isArray(e.lotes) ? e.lotes : []).length;
                      return (
                        <Card key={vendedor.name} className="bg-card border border-border rounded-xl shadow-none">
                          <CardContent className="pt-6 pb-2 flex flex-col gap-2">
                            <div className="flex justify-between items-start">
                              <div>
                                <h4 className="font-semibold text-lg text-gray-900 mb-0.5">{vendedor.name}</h4>
                                <div className="text-gray-700 text-sm mb-0.5">{vendedor.phone}</div>
                                <div className="text-gray-600 text-sm mb-0.5">{lotesVendidos} lote{lotesVendidos === 1 ? '' : 's'} vendidos</div>
                                <div className="text-gray-500 text-sm mb-0.5">Rangos asignados:</div>
                                <div className="flex gap-2 flex-wrap mb-2">
                                  {vendedor.ranges.map((r: { from: string; to: string }, idx: number) => (
                                    <span
                                      key={idx}
                                      className="bg-white border border-gray-200 shadow-sm rounded-lg px-2 py-0.5 text-xs text-gray-700 font-medium"
                                      style={{ minWidth: 48, textAlign: 'center', letterSpacing: 0.5 }}
                                    >
                                      {r.from}-{r.to}
                                    </span>
                                  ))}
                                </div>
                              </div>
                              <div className="text-right space-y-1">
                                {/* Mostrar solo la información entre paréntesis, sin labels */}
                                <div className="flex flex-col items-end gap-1">
                                  <div className="text-green-600 font-bold text-lg">Q. {comisionesPagadas.toLocaleString(undefined, { minimumFractionDigits: 2 })} <span className="text-xs font-normal">({comisionesPagadasArr.length} pagadas)</span></div>
                                  <div className="text-orange-600 font-bold text-lg">Q. {comisionesPendientes.toLocaleString(undefined, { minimumFractionDigits: 2 })} <span className="text-xs font-normal">({comisionesPendientesArr.length} pendientes)</span></div>
                                  <div className="text-blue-700 font-bold text-lg">Q. {totalComisiones.toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
                                </div>
                              </div>
                            </div>
                            <hr className="my-2 border-gray-200" />
                            <div className="flex justify-end gap-2 mt-1">
                              <Button variant="outline" className="border border-gray-200 hover:border-gray-300" onClick={() => { setVendedorEditando(vendedor); setVendedorModalOpen(true); }}>
                                <span className="flex items-center gap-1"><svg xmlns='http://www.w3.org/2000/svg' className='h-4 w-4' fill='none' viewBox='0 0 24 24' stroke='currentColor'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M15.232 5.232l3.536 3.536M9 13h3l8-8a2.828 2.828 0 10-4-4l-8 8v3z' /></svg>Editar</span>
                              </Button>
                              <Button variant="outline" className="border border-gray-200 hover:border-gray-300 text-red-600" onClick={() => setVendedorAEliminar(vendedor)}>
                                <span className="flex items-center gap-1"><svg xmlns='http://www.w3.org/2000/svg' className='h-4 w-4' fill='none' viewBox='0 0 24 24' stroke='currentColor'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M6 18L18 6M6 6l12 12' /></svg>Eliminar</span>
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                </div>
                <VendedorModal
                  open={vendedorModalOpen}
                  onClose={() => {
                    setVendedorModalOpen(false);
                    setVendedorEditando(null);
                  }}
                  onSave={async (nuevo: Vendedor) => {
                    if (sellers.some((v: any) => v.name.trim().toLowerCase() === nuevo.name.trim().toLowerCase() && (!vendedorEditando || v.name !== vendedorEditando.name))) {
                      alert('Ya existe un vendedor con ese nombre.');
                      return;
                    }
                    try {
                      if (vendedorEditando && vendedorEditando.id) {
                        await updateSeller(vendedorEditando.id, nuevo);
                      } else {
                        await addSellerWithUser(nuevo);
                      }
                      setVendedorEditando(null);
                    } catch (e: any) {
                      console.error('Error saving seller:', e);
                      alert(`Error al guardar el vendedor: ${e.message || 'Error desconocido'}`);
                    }
                  }}
                  vendedor={vendedorEditando}
                />
                {/* Modal de confirmación de eliminación */}
                <Dialog open={!!vendedorAEliminar} onOpenChange={(v: boolean) => { if (!v) setVendedorAEliminar(null); }}>
                  <DialogContent className="max-w-md bg-white">
                    <DialogHeader>
                      <DialogTitle>¿Estás seguro?</DialogTitle>
                      <DialogDescription>
                        Esta acción no se puede deshacer. Se eliminará permanentemente al vendedor <b>{vendedorAEliminar?.name}</b> y toda su información asociada.
                      </DialogDescription>
                    </DialogHeader>
                    {vendedorAEliminar && (
                      (() => {
                        const lotesVendidos = lots.filter((l: any) => l.seller === vendedorAEliminar.name && (l.status === 'vendido' || l.status === 'crédito'));
                        return (
                          <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 rounded p-3 flex items-start gap-2 mb-4">
                            <svg className="h-5 w-5 mt-0.5 flex-shrink-0 text-yellow-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M12 20a8 8 0 100-16 8 8 0 000 16z" /></svg>
                            <div>
                              <b>Advertencia:</b> {lotesVendidos.length > 0 ? `Este vendedor tiene ${lotesVendidos.length} lote${lotesVendidos.length === 1 ? '' : 's'} vendidos. Asegúrate de transferir sus ventas a otro vendedor antes de eliminarlo.` : 'Esta acción es irreversible.'}
                            </div>
                          </div>
                        );
                      })()
                    )}
                    <div className="flex justify-end gap-2 mt-4">
                      <Button variant="outline" className="border border-gray-200 hover:border-gray-300" onClick={() => setVendedorAEliminar(null)}>
                        Cancelar
                      </Button>
                      <Button variant="destructive" className="bg-red-600 hover:bg-red-700 text-white" onClick={async () => {
                        if (vendedorAEliminar && vendedorAEliminar.id) {
                          try {
                            await deleteSeller(vendedorAEliminar.id);
                          } catch (e) {
                            alert('Error al eliminar el vendedor.');
                          }
                        }
                        setVendedorAEliminar(null);
                      }}>
                        Eliminar Vendedor
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </>
            )}

            {activeTab === 'usuarios' && (
              <div className="space-y-6">
                <UsuariosTable />
              </div>
            )}
            {activeTab === 'movimientos' && (
              <MovimientosTable abrirNuevoMovimiento={abrirNuevoMovimiento} setAbrirNuevoMovimiento={setAbrirNuevoMovimiento} />
            )}
            {activeTab === 'estado-cuenta' && <EstadoDeCuenta />}
            {activeTab === 'estado-cuenta-bancario' && <EstadoCuentaBancario />}
            {activeTab === 'cobros' && <PaymentSystem />}
            {activeTab === 'reporte-cobros' && (
              // Cargado de manera lazy para no aumentar el bundle inicial
              (() => {
                const Reporte = require('./components/ReporteCobrosMensuales').default;
                return <Reporte />;
              })()
            )}
            {activeTab === 'chat-ia' && <ChatIA />}

            {activeTab === 'devoluciones' && <Devoluciones />}
          </main>
        </div>
      </ThemeProvider >
    </NotificationProvider >
  );
}

// Componente para manejar el login de compradores
function CompradorLogin() {
  const [user, setUser] = useState<null | { userType: string; userInput: string; pin: string }>(() => {
    const savedUser = localStorage.getItem('miltierrasUser');
    return savedUser ? JSON.parse(savedUser) : null;
  });
  const { usuarios } = useAppData();

  // Validación robusta de usuario autenticado - solo validar si ya está autenticado
  React.useEffect(() => {
    if (user && user.pin && usuarios && usuarios.length > 0) {
      // Buscar usuario comprador válido
      const lotesInput = user.userInput?.split(',').map((l: string) => l.trim()) || [];
      const usuario = usuarios.find((u: any) =>
        u.tipo === 'comprador' &&
        u.pin === user.pin &&
        u.activo &&
        !u.bloqueado &&
        u.lotesAsignados?.some((lote: string) => lotesInput.includes(lote))
      );
      if (!usuario) {
        // Solo limpiar si ya se intentó autenticar (tiene PIN)
        localStorage.removeItem('miltierrasUser');
        setUser(null);
      }
    }
  }, [user, usuarios]);

  const handleLoginSuccess = (userData: { userType: string; userInput: string; pin?: string; password?: string; userId?: string }) => {
    setUser({
      userType: userData.userType,
      userInput: userData.userInput,
      pin: userData.pin || ''
    });
    localStorage.setItem('miltierrasUser', JSON.stringify({
      userType: userData.userType,
      userInput: userData.userInput,
      pin: userData.pin || ''
    }));
  };

  if (!user) {
    return <LoginScreen userType="comprador" onLoginSuccess={handleLoginSuccess} />;
  }

  return <CompradorDashboard user={user} />;
}

// Componente para manejar el login de vendedores
function VendedorLogin() {
  const [user, setUser] = useState<null | { userType: string; userInput: string; pin: string }>(() => {
    const savedUser = localStorage.getItem('miltierrasUser');
    return savedUser ? JSON.parse(savedUser) : null;
  });
  const { usuarios, sellers } = useAppData();

  // Validación robusta de usuario vendedor autenticado
  React.useEffect(() => {
    if (user && usuarios && sellers && usuarios.length > 0 && sellers.length > 0) {
      // Buscar vendedor por nombre (primer nombre) y pin
      const inputName = user.userInput?.trim().toLowerCase();
      const vendedor = sellers.find((seller: { name: string }) => seller.name.split(' ')[0].toLowerCase() === inputName);
      if (!vendedor) {
        localStorage.removeItem('miltierrasUser');
        setUser(null);
        return;
      }
      const usuarioVendedor = usuarios.find((u: any) =>
        u.tipo === 'vendedor' &&
        u.nombre === vendedor.name &&
        u.pin === user.pin &&
        u.activo &&
        !u.bloqueado
      );
      if (!usuarioVendedor) {
        localStorage.removeItem('miltierrasUser');
        setUser(null);
      }
    }
  }, [user, usuarios, sellers]);

  const handleLoginSuccess = (userData: { userType: string; userInput: string; pin?: string; password?: string; userId?: string }) => {
    setUser({
      userType: userData.userType,
      userInput: userData.userInput,
      pin: userData.pin || ''
    });
    localStorage.setItem('miltierrasUser', JSON.stringify({
      userType: userData.userType,
      userInput: userData.userInput,
      pin: userData.pin || ''
    }));
  };

  if (!user) {
    return <LoginScreen userType="vendedor" onLoginSuccess={handleLoginSuccess} />;
  }

  return <VendedorDashboard user={user} />;
}

// Componente para manejar el login de socios
function SocioLogin() {
  const [user, setUser] = useState<null | { userType: string; userInput: string; password?: string }>(() => {
    const savedUser = localStorage.getItem('miltierrasUser');
    return savedUser ? JSON.parse(savedUser) : null;
  });
  const { usuarios, lots, expenses, config } = useAppData();

  // Validación robusta de usuario socio autenticado
  React.useEffect(() => {
    if (user && usuarios && usuarios.length > 0) {
      // Buscar socio por nombre (case insensitive) y password
      const socio = usuarios.find((u: any) =>
        u.tipo === 'socio' &&
        u.nombre.toLowerCase().trim() === user.userInput.toLowerCase().trim() &&
        u.password === user.password &&
        u.activo &&
        !u.bloqueado
      );
      if (!socio) {
        localStorage.removeItem('miltierrasUser');
        setUser(null);
      }
    }
  }, [user, usuarios]);

  const handleLoginSuccess = (userData: { userType: string; userInput: string; pin?: string; password?: string; userId?: string }) => {
    setUser({
      userType: userData.userType,
      userInput: userData.userInput.trim(),
      password: userData.password || ''
    });
    localStorage.setItem('miltierrasUser', JSON.stringify({
      userType: userData.userType,
      userInput: userData.userInput.trim(),
      password: userData.password || ''
    }));
  };

  if (!user) {
    return <LoginScreen userType="socio" onLoginSuccess={handleLoginSuccess} />;
  }

  console.log('SocioLogin render - user:', user);
  console.log('SocioLogin render - user exists:', !!user);

  if (!user) {
    console.log('SocioLogin render - showing LoginScreen');
    return <LoginScreen userType="socio" onLoginSuccess={handleLoginSuccess} />;
  }

  console.log('SocioLogin render - showing SocioDashboard');
  const months = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
  const ingresos = expenses.filter((e: any) => ['ingreso', 'contado', 'enganche', 'abono'].includes((e.tipo || '').toLowerCase()));
  const egresos = expenses.filter((e: any) => ['egreso', 'comision'].includes((e.tipo || '').toLowerCase()));
  const totalIngresos = ingresos.reduce((sum: number, e: any) => sum + (parseFloat(e.monto) || 0), 0);
  const totalEgresos = egresos.reduce((sum: number, e: any) => sum + (parseFloat(e.monto) || 0), 0);
  const utilidadBruta = totalIngresos - totalEgresos;
  const proyeccionAnual = lots.filter((l: any) => l.status === 'vendido' || l.status === 'crédito').reduce((sum: number, l: any) => sum + (l.totalValue || 0), 0);
  // Agrupar ingresos por mes
  const ingresosPorMes: { [k: string]: number } = {};
  ingresos.forEach((e: any) => {
    const mes = e.fecha ? months[new Date(e.fecha).getMonth()] : 'Sin fecha';
    ingresosPorMes[mes] = (ingresosPorMes[mes] || 0) + (parseFloat(e.monto) || 0);
  });
  const mesesConIngresos = Object.keys(ingresosPorMes).length;
  const promedioMensual = mesesConIngresos > 0 ? totalIngresos / mesesConIngresos : 0;
  // Mejor mes
  let mejorMes = '-';
  let maxIngreso = 0;
  Object.entries(ingresosPorMes).forEach(([mes, valor]) => {
    if (valor > maxIngreso) { maxIngreso = valor; mejorMes = mes; }
  });
  // Eficiencia: % cobrado respecto a proyección
  const eficiencia = proyeccionAnual > 0 ? (totalIngresos / proyeccionAnual) * 100 : 0;

  const vendidos = lots
    .filter((lot: any) => lot.status === 'vendido')
    .reduce((sum: number, lot: any) => {
      if (Array.isArray(lot.idLotes) && lot.idLotes.length > 1) {
        return sum + lot.idLotes.length;
      }
      return sum + 1;
    }, 0);
  const credito = lots
    .filter((lot: any) => lot.status === 'crédito')
    .reduce((sum: number, lot: any) => {
      if (Array.isArray(lot.idLotes) && lot.idLotes.length > 1) {
        return sum + lot.idLotes.length;
      }
      return sum + 1;
    }, 0);
  const totalLotes = config?.lotes || 0;
  const disponibles = totalLotes - vendidos - credito;
  const stats = {
    vendidos,
    disponibles: disponibles >= 0 ? disponibles : 0,
    credito
  };

  return <SocioDashboard
    totalLotes={totalLotes}
    stats={stats}
    financial={{ totalIngresos, totalEgresos, utilidadBruta, proyeccionAnual, promedioMensual, mejorMes, eficiencia }}
    nombre={user.userInput}
    pin={user.password}
    autenticado={true}
  />;
}

// Componente para manejar el login de contabilidad
function ContabilidadLogin() {
  const [user, setUser] = useState<null | { userType: string; userInput: string; password?: string }>(() => {
    const savedUser = localStorage.getItem('miltierrasUser');
    return savedUser ? JSON.parse(savedUser) : null;
  });
  const { usuarios, expenses } = useAppData();

  // Validación robusta de usuario contabilidad autenticado
  React.useEffect(() => {
    if (user && usuarios && usuarios.length > 0) {
      // Buscar usuario contabilidad por nombre (case insensitive) y password
      const contabilidad = usuarios.find((u: any) =>
        u.tipo === 'contabilidad' &&
        u.nombre.toLowerCase().trim() === user.userInput.toLowerCase().trim() &&
        u.password === user.password &&
        u.activo &&
        !u.bloqueado
      );
      if (!contabilidad) {
        localStorage.removeItem('miltierrasUser');
        setUser(null);
      }
    }
  }, [user, usuarios]);

  const handleLoginSuccess = (userData: { userType: string; userInput: string; pin?: string; password?: string; userId?: string }) => {
    setUser({
      userType: userData.userType,
      userInput: userData.userInput.trim(),
      password: userData.password || ''
    });
    localStorage.setItem('miltierrasUser', JSON.stringify({
      userType: userData.userType,
      userInput: userData.userInput.trim(),
      password: userData.password || ''
    }));
  };

  if (!user) {
    return <LoginScreen userType="contabilidad" onLoginSuccess={handleLoginSuccess} />;
  }

  return <MainDashboard />;
}

export default function App() {
  const { lots, expenses, setLots, setExpenses } = useAppData();

  // Estado de autenticación global para el enrutamiento
  const [user, setUser] = useState<any>(() => {
    const savedUser = localStorage.getItem('bimobiUser');
    return savedUser ? JSON.parse(savedUser) : null;
  });

  // Efecto para sincronización global de abonos
  useEffect(() => {
    if (lots.length > 0 && expenses.length > 0) {
      syncAbonosGlobal(lots, setLots, expenses, setExpenses);
    }
  }, [lots, expenses, setLots, setExpenses]);

  const handleLogin = (userData: any) => {
    setUser(userData);
    localStorage.setItem('bimobiUser', JSON.stringify(userData));
  };

  // Lógica de enrutamiento basada en subdominio
  const hostname = window.location.hostname;
  const searchParams = new URLSearchParams(window.location.search);
  const forceLanding = searchParams.get('force') === 'landing';

  // Si el dominio contiene 'demo', es la app. EXCLUIMOS explícitamente bimobiapp.web.app y localhost (si se desea)
  // También si force=landing está presente, forzamos modo comercial
  const isDemo = !forceLanding && hostname.includes('demo') && hostname !== 'bimobiapp.web.app';

  return (
    <Router>
      <div className="min-h-screen bg-background font-sans antialiased">
        <Routes>
          <Route path="/chat-publico" element={<PublicChatIA />} />

          {/* Ruta para Login explícito */}
          <Route path="/login" element={
            !user ? (
              <LoginScreen userType="admin" onLoginSuccess={handleLogin} />
            ) : (
              <Navigate to="/app" replace />
            )
          } />

          {/* Ruta principal: 
              - Si es Demo: Redirige a App (que redirigirá a Login si no hay sesión)
              - Si es Comercial: Muestra Landing Page
          */}
          <Route path="/" element={
            isDemo ? (
              <Navigate to="/app" replace />
            ) : (
              <LandingPage />
            )
          } />

          {/* Ruta de la aplicación protegida */}
          <Route path="/app/*" element={
            user ? (
              <MainDashboard />
            ) : (
              <Navigate to="/login" replace />
            )
          } />

          {/* Rutas legacy para otros roles */}
          <Route path="/admin" element={<Navigate to="/app" replace />} />
          <Route path="/compradores" element={<CompradorLogin />} />
          <Route path="/vendedores" element={<VendedorLogin />} />
          <Route path="/socios" element={<SocioLogin />} />
          <Route path="/contabilidad" element={<ContabilidadLogin />} />

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <Toaster />
        {/* Mode Indicator */}
        <div className="fixed bottom-1 right-1 text-[10px] text-gray-300 opacity-50 pointer-events-none z-[9999]">
          {isDemo ? 'DEMO' : 'COM'} | {hostname}
        </div>
      </div>
    </Router>
  );
}